import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import QuickExpense from "../components/QuickExpense";
import NotificationBell from "../components/NotificationBell";
import {
  LayoutDashboard, UtensilsCrossed, CalendarDays, Package, ClipboardCheck, ClipboardList, Euro, Users,
  LogOut, ExternalLink, Menu, X, CalendarClock, UploadCloud, Truck, Contact, Boxes, Settings, Calculator,
  TrendingUp, ShoppingBag, ConciergeBell, Wine, ChefHat, Sparkles, Link2, ScrollText, AlertTriangle, ChevronDown,
  Activity, Factory, Receipt, Cog, QrCode, Gift,
} from "lucide-react";

const GROUPS = [
  { key: "operativita", label: "Operatività", icon: Activity, items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/menu-gestione", label: "Menù Digitale", icon: UtensilsCrossed, module: "menu" },
    { to: "/ordini", label: "Tavoli & Ordini", icon: ConciergeBell, module: "ordini" },
    { to: "/pos", label: "Cassa POS", icon: Calculator, module: "ordini" },
    { to: "/qr-codes", label: "QR Codes", icon: QrCode, roles: ["admin", "manager"] },
    { to: "/loyalty", label: "Club Dogana", icon: Gift, module: "ordini" },
    { to: "/prenotazioni", label: "Prenotazioni", icon: CalendarDays, module: "prenotazioni" },
    { to: "/clienti", label: "Clienti", icon: Contact, module: "clienti" },
  ] },
  { key: "produzione", label: "Produzione", icon: Factory, items: [
    { to: "/ordini/bar", label: "Monitor Bar", icon: Wine, module: "ordini" },
    { to: "/ordini/cucina", label: "Monitor Cucina", icon: ChefHat, module: "ordini" },
    { to: "/ordini/non-assegnati", label: "KDS · Non assegnati", icon: AlertTriangle, module: "ordini" },
    { to: "/magazzino", label: "Magazzino", icon: Package, module: "magazzino" },
    { to: "/magazzino/produzioni", label: "Produzioni", icon: Factory, module: "magazzino" },
    { to: "/magazzino/inventari", label: "Inventari", icon: ClipboardList, module: "magazzino" },
    { to: "/haccp", label: "HACCP", icon: ClipboardCheck, module: "haccp" },
  ] },
  { key: "foodcost", label: "Food Cost", icon: Receipt, items: [
    { to: "/food-cost", label: "Food Cost / Dashboard", icon: Calculator, module: "contabilita", end: true },
    { to: "/food-cost/ingredienti", label: "Ingredienti", icon: Boxes, roles: ["admin", "manager"] },
    { to: "/food-cost/prodotti", label: "Prodotti da associare", icon: Link2, roles: ["admin", "manager"] },
    { to: "/food-cost/ricette", label: "Ricette", icon: ScrollText, roles: ["admin", "manager"] },
    { to: "/food-cost/import", label: "Importa fatture", icon: UploadCloud, roles: ["admin", "manager"] },
  ] },
  { key: "amministrazione", label: "Amministrazione", icon: TrendingUp, items: [
    { to: "/fornitori", label: "Fornitori", icon: Truck, module: "fornitori" },
    { to: "/contabilita", label: "Contabilità", icon: Euro, module: "contabilita" },
    { to: "/analisi", label: "Analisi", icon: TrendingUp, module: "contabilita" },
    { to: "/vendite", label: "Vendite", icon: ShoppingBag, module: "contabilita" },
    { to: "/import-cassa", label: "Import Cassa", icon: UploadCloud, module: "import-cassa" },
    { to: "/turni", label: "Turni", icon: CalendarClock, module: "turni" },
    { to: "/staff", label: "Staff", icon: Users, module: "staff" },
  ] },
  { key: "sistema", label: "Sistema", icon: Cog, items: [
    { to: "/dogana-ai", label: "Dogana AI", icon: Sparkles, roles: ["admin", "manager"] },
    { to: "/impostazioni", label: "Impostazioni", icon: Settings, admin: true },
  ] },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  const handleLogout = async () => { await logout(); toast.success("Disconnesso"); navigate("/login"); };

  const perms = user?.role === "admin" ? null : (user?.permissions || []);
  const canSee = (n) => n.roles ? n.roles.includes(user?.role) : (n.admin ? user?.role === "admin" : (!n.module || perms === null || perms.includes(n.module)));
  const groups = GROUPS.map((g) => ({ ...g, items: g.items.filter(canSee) })).filter((g) => g.items.length > 0);

  const isItemActive = (it) => it.end ? location.pathname === it.to : location.pathname === it.to || location.pathname.startsWith(it.to + "/");
  const activeGroupKey = groups.find((g) => g.items.some(isItemActive))?.key;

  useEffect(() => { if (activeGroupKey) setOpenGroup(activeGroupKey); }, [activeGroupKey]);

  return (
    <div className="min-h-screen bg-secondary/40">
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-border flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src="/logo-icon.png" alt="Dogana 2.0" className="w-8 h-8 rounded-lg" />
          <span className="font-dash font-semibold">Dogana 2.0</span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell suffix="-mobile" />
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="mobile-menu-button"><Menu className="w-5 h-5" /></Button>
        </div>
      </header>

      {open && <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setOpen(false)} data-testid="mobile-overlay" />}

      <aside className={`fixed top-0 left-0 h-screen w-64 bg-white border-r border-border flex flex-col z-50 transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-icon.png" alt="Dogana 2.0" className="w-9 h-9 rounded-lg" />
            <img src="/logo-dogana.png" alt="Dogana 2.0" className="h-5 w-auto hidden sm:block" style={{ filter: "brightness(0.55)" }} />
          </div>
          <div className="flex items-center gap-1">
            <div className="hidden lg:block"><NotificationBell /></div>
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(false)} data-testid="close-menu-button"><X className="w-5 h-5" /></Button>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {groups.map((g) => {
            const isOpen = openGroup === g.key;
            const groupActive = g.key === activeGroupKey;
            return (
              <div key={g.key}>
                <button
                  onClick={() => setOpenGroup(isOpen ? null : g.key)}
                  data-testid={`navgroup-${g.key}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${groupActive ? "text-primary" : "text-foreground hover:bg-secondary"}`}>
                  <g.icon className="w-4 h-4" />
                  <span className="flex-1 text-left uppercase tracking-wide text-xs">{g.label}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="mt-1 ml-3 pl-3 border-l border-border space-y-0.5">
                    {g.items.map((n) => (
                      <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                        data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                        className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                        <n.icon className="w-4 h-4" /> {n.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border space-y-2">
          <a href="/menu" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors px-4" data-testid="open-public-menu-link">
            <ExternalLink className="w-3.5 h-3.5" /> Menù pubblico
          </a>
          <div className="flex items-center justify-between px-2 pt-2">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </aside>

      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8 min-h-screen"><Outlet /></main>
      {(user?.role === "admin" || (user?.permissions || []).includes("contabilita")) && <QuickExpense />}
    </div>
  );
}
